'use client';

import React, { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';
import { useCart } from './CartProvider';
import Logo from './Logo';
import LanguageSwitcher from './LanguageSwitcher';
import AccountMenu from './AccountMenu';
import { Link, usePathname } from '../i18n/navigation';

/**
 * O item do menu correspondente a pagina atual fica destacado. Alem da rota
 * exata, um item tambem cobre suas sub-rotas (`/artigos/algum-post` mantem
 * "Artigos" aceso) e, quando a secao tem URLs com outro prefixo, os extras
 * declarados em `extras` - o catalogo, por exemplo, leva a `/produto/[slug]`.
 */
function rotaAtiva(pathname: string, href: string, extras: string[] = []): boolean {
  // A home so acende em match exato: todo caminho comeca com "/".
  if (href === '/') return pathname === '/';
  return [href, ...extras].some(
    (base) => pathname === base || pathname.startsWith(`${base}/`)
  );
}

// ─── Header Component ─────────────────────────────────────────────────────────
export default function SiteHeader() {
  const t = useTranslations();
  // usePathname do next-intl devolve o caminho SEM o prefixo de locale, entao
  // a comparacao funciona igual em /artigos, /en/artigos e /es/artigos.
  const pathname = usePathname();
  // `oculto: true` tira o item do menu (desktop e mobile, que leem esta mesma
  // lista) sem apagar a rota, a traducao nem o sitemap - as paginas
  // continuam no ar e indexaveis, so deixam de ter link no cabecalho. Para
  // reexibir, basta remover a flag.
  const navLinks = [
    { label: t('nav.inicio'), href: '/' },
    { label: t('nav.produtos'), href: '/produtos' },
    { label: t('nav.catalogo'), href: '/catalogo', extras: ['/produto'] },
    { label: t('nav.artigos'), href: '/artigos', oculto: true },
    { label: t('nav.midia'), href: '/midia' },
    { label: t('nav.cases'), href: '/cases' },
    { label: t('nav.contato'), href: '/contato' },
  ].filter((link) => !('oculto' in link && link.oculto));
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { count } = useCart();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <header
      className={`sticky top-0 z-50 transition-shadow duration-200 ${scrolled ? 'shadow-md' : ''}`}
      style={{ backgroundColor: '#eeeeee' }}
    >
      <div className="container-site">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link href="/" className="flex items-center gap-2 flex-shrink-0">
            <Logo style={{ height: '36px', width: 'auto' }} />
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-1">
            {navLinks.map((link) => {
              const ativo = rotaAtiva(pathname, link.href, link.extras);
              return (
                <Link
                  key={link.label}
                  href={link.href}
                  className={`nav-link${ativo ? ' nav-link-ativo' : ''}`}
                  aria-current={ativo ? 'page' : undefined}
                >
                  {link.label}
                </Link>
              );
            })}
          </nav>

          {/* Utilities */}
          <div className="flex items-center gap-3">
            <LanguageSwitcher />
            <button
              aria-label={t('nav.buscarAria')}
              className="p-2 hover:bg-gray-200 rounded transition-colors duration-150"
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <circle cx="11" cy="11" r="8" />
                <path d="m21 21-4.35-4.35" />
              </svg>
            </button>
            <AccountMenu />
            <Link
              href="/carrinho"
              aria-label={t('nav.carrinhoAria')}
              className="p-2 hover:bg-gray-200 rounded transition-colors duration-150 relative inline-block"
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
                <line x1="3" y1="6" x2="21" y2="6" />
                <path d="M16 10a4 4 0 0 1-8 0" />
              </svg>
              {count > 0 && (
                <span
                  className="absolute -top-1 -right-1 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center"
                  style={{ backgroundColor: '#000', fontSize: '10px' }}
                >
                  {count > 9 ? '9+' : count}
                </span>
              )}
            </Link>
            {/* Mobile menu button */}
            <button
              className="md:hidden p-2 hover:bg-gray-200 rounded transition-colors duration-150"
              onClick={() => setMenuOpen(!menuOpen)}
              aria-label={t('nav.menuAria')}
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                {menuOpen ? (
                  <>
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </>
                ) : (
                  <>
                    <line x1="3" y1="6" x2="21" y2="6" />
                    <line x1="3" y1="12" x2="21" y2="12" />
                    <line x1="3" y1="18" x2="21" y2="18" />
                  </>
                )}
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile Nav */}
        {menuOpen && (
          <div className="md:hidden border-t border-gray-300 py-2">
            {navLinks.map((link) => {
              const ativo = rotaAtiva(pathname, link.href, link.extras);
              return (
                <Link
                  key={link.label}
                  href={link.href}
                  className={`block px-4 py-2 text-sm hover:bg-gray-200 transition-colors duration-150${
                    ativo ? ' nav-link-mobile-ativo' : ''
                  }`}
                  aria-current={ativo ? 'page' : undefined}
                  onClick={() => setMenuOpen(false)}
                >
                  {link.label}
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </header>
  );
}
